//COMPONENTE: HistogramaComponent

import { Component, NgZone, AfterViewInit, effect, WritableSignal, signal, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SharedDataService } from '../../../../shared/services/shared-data.service';
import { TableRow } from '../../../../shared/models/components/tables/table.models';
import * as d3 from 'd3';

@Component({
    selector: 'app-histograma',
    templateUrl: './histograma.component.html',
    styleUrls: ['./histograma.component.scss'],
    imports: []
})
export class HistogramaComponent {
    @Input() dataKey!: string;
    rowsSummary: WritableSignal<TableRow[]> = signal([]);
    rowsAllTrades: WritableSignal<TableRow[]> = signal([]);
    filteredRows: WritableSignal<TableRow[]> = signal([]);
    originalRowsAllTrades: TableRow[] = [];
    rowsAllTradesModelAccuracy: WritableSignal<TableRow[]> = signal([]);
    originalRowsAllTradesModelAccuracy: TableRow[] = [];
    private resizeObserver!: ResizeObserver;


    private zone = inject(NgZone);
    private sharedDataService = inject(SharedDataService);
    constructor() {
        effect(() => {

            if (this.dataKey === 'tradingStrategy') {
                this.rowsAllTrades(); // Observa cambios en rowsAllTrades
                this.rowsAllTrades = this.sharedDataService.getRowsAllTrades();
                this.originalRowsAllTrades = this.sharedDataService.getOriginalRowsAllTrades();

            } else if (this.dataKey === 'modelAccuracy') {
                this.rowsAllTradesModelAccuracy(); // Observa cambios en rowsAllTradesModelAccuracy
                this.rowsAllTradesModelAccuracy = this.sharedDataService.getRowsAllTradesModelAccuracy();
                this.originalRowsAllTradesModelAccuracy = this.sharedDataService.getOriginalRowsAllTradesModelAccuracy();
            }
            this.updateChart(); // Actualiza el histograma
        });
    }

    //  ngAfterViewInit() {
    //   this.zone.runOutsideAngular(() => {
    //     setTimeout(() => {
    //       this.createHistogram();
    //     });
    //   });
    // }

    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => {
            const container = document.getElementById('histogram-chart');
            if (!container) return;

            this.resizeObserver = new ResizeObserver(() => {
                this.createHistogram();
            });
            this.resizeObserver.observe(container);

            this.createHistogram();
        });
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }


    ngOnInit() {
        if (this.dataKey === 'tradingStrategy') {
            this.rowsAllTrades = this.sharedDataService.getRowsAllTrades();
            this.rowsSummary = this.sharedDataService.getRowsSummary();
            this.originalRowsAllTrades = [...this.rowsAllTrades()];

        } else if (this.dataKey === 'modelAccuracy') {

            this.rowsAllTradesModelAccuracy = this.sharedDataService.getRowsAllTradesModelAccuracy();
            this.originalRowsAllTradesModelAccuracy = [...this.rowsAllTradesModelAccuracy()];
        }
    }




    private createHistogram(): void {

        const currentHistogram = d3.select('#histogram-chart');
        currentHistogram.selectAll('*').remove();

        const containerElement = currentHistogram.node() as HTMLElement;
        const containerWidth = containerElement.clientWidth;
        const containerHeight = containerElement.clientHeight;

        const margin = { top: 20, right: 20, bottom: 40, left: 40 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        const svgElement = currentHistogram
            .append('svg')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`);

        const svg = svgElement.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const pandlValues = this.getPandLValues();
        const min = d3.min(pandlValues) ?? 0;
        const max = d3.max(pandlValues) ?? 1;
        const range = max - min;

        let numberOfBins: number;
        if (pandlValues.length <= 10) {
            numberOfBins = pandlValues.length;
        } else if (pandlValues.length <= 50) {
            numberOfBins = 10;
        } else if (pandlValues.length <= 100) {
            numberOfBins = 15;
        } else {
            numberOfBins = Math.ceil(Math.log2(pandlValues.length) + 1);
            numberOfBins = Math.min(Math.max(numberOfBins, 15), 30);
        }

        const binWidth = range / numberOfBins;
        const thresholds = d3.range(min, max + binWidth, binWidth);

        const histogram = d3.histogram()
            .domain([min, max])
            .thresholds(thresholds);


        const bins = histogram(pandlValues);

        const x = d3.scaleLinear()
            .domain([min, max])
            .range([0, width]);

        const y = d3.scaleLinear()
            .domain([0, d3.max(bins, d => d.length) || 1])
            .range([height - margin.top - margin.bottom, 0]);

        // Evita borrar tooltips de otros componentes
        d3.selectAll('.hist-tooltip').remove();

        const tooltipContainer = d3.select('body')
            .append('div')
            .attr('class', 'hist-tooltip')
            .style('opacity', 0)
            .style('visibility', 'hidden')
            .style('position', 'fixed') // no expande la pantalla
            .style('z-index', '9999')
            .style('background-color', 'black')
            .style('color', 'white')
            .style('padding', '5px 10px')
            .style('border-radius', '5px')
            .style('pointer-events', 'none')
            .style('max-width', '320px')
            .style('white-space', 'normal');

        let hideTimer: number | undefined;

        const showTooltip = () => {
            if (hideTimer) {
                clearTimeout(hideTimer);
                hideTimer = undefined;
            }
            tooltipContainer.style('visibility', 'visible').style('opacity', 1);
        };

        const hideTooltip = () => {
            hideTimer = window.setTimeout(() => {
                tooltipContainer.style('opacity', 0).style('visibility', 'hidden');
            }, 120); // pequeño delay para evitar parpadeo entre barras
        };

        const placeTooltip = (event: PointerEvent) => {
            const el = tooltipContainer.node() as HTMLDivElement;
            const padding = 8;
            const ttW = el?.offsetWidth || 0;
            const ttH = el?.offsetHeight || 0;

            let x = event.clientX + 12;
            let y = event.clientY - ttH - 12;

            if (x + ttW + padding > window.innerWidth) x = event.clientX - ttW - 12;
            if (x < padding) x = padding;

            if (y < padding) y = event.clientY + 12;
            if (y + ttH + padding > window.innerHeight) {
                y = Math.max(padding, window.innerHeight - ttH - padding);
            }

            tooltipContainer.style('left', `${x}px`).style('top', `${y}px`);
        };

        svg.selectAll('rect')
            .data(bins)
            .enter()
            .append('rect')
            .attr('x', d => x(d.x0 || 0))
            .attr('y', d => y(d.length))
            .attr('width', d => Math.max(0, x(d.x1 || 0) - x(d.x0 || 0)))
            .attr('height', d => height - margin.top - margin.bottom - y(d.length))
            .style('fill', '#E5601F')
            .on('pointerenter', function (event, d) {
                d3.select(this).style('fill', '#711B00');
                tooltipContainer.html(`Interval: ${d.x0} - ${d.x1}<br>Total: ${d.length}`);
                showTooltip();
                placeTooltip(event as PointerEvent);
            })
            .on('pointermove', function (event) {
                placeTooltip(event as PointerEvent);
            })
            .on('pointerleave', function () {
                hideTooltip();
                d3.select(this).style('fill', '#E5601F');
            })
            .on('click', (event, d) => {
                const x0 = d.x0 ?? 0;
                const x1 = d.x1 ?? 0;
                this.filterRows(x0, x1);
            });

        const xAxisLabel = this.dataKey === 'tradingStrategy' ? 'Losses and Profit' : 'Intervals';
        
        svg.append('g')
            .attr('transform', `translate(0,${height - margin.top - margin.bottom})`)
            .call(d3.axisBottom(x))
            .append('text')
            .attr('x', width / 2)
            .attr('y', margin.bottom - 5)
            .attr('text-anchor', 'middle')
            .style('font-size', '14px')
            .style('font-family', 'Gotham Bold, sans-serif')
            .style('fill', 'black')
            .text(xAxisLabel);


        // Ticks
        const maxFrequency = d3.max(bins, d => d.length) || 1;
        // const maxFrequency = d3.max(bins, d => d.length) || 0;
        const tickStep = 1;
        // const tickStep = useOneBinPerValue ? 1 : 5;
        const tickValues = d3.range(0, maxFrequency + tickStep, tickStep);

        const yAxisLabel = this.dataKey === 'tradingStrategy' ? 'Trades Quantity' : 'Frequency';
        
        svg.append('g')
            .call(d3.axisLeft(y)
                .tickValues(tickValues)
                .tickFormat(d3.format('d'))
            )
            .append('text')
            .attr('transform', 'rotate(-90)')
            .attr('x', -height / 2)
            .attr('y', -margin.left + 10)
            .attr('text-anchor', 'middle')
            .style('font-size', '14px')
            .style('fill', 'black')
            .text(yAxisLabel);
    }



    private updateChart(): void {
        this.createHistogram();
    }

    private getPandLValues(): number[] {

        if (this.dataKey === 'tradingStrategy') {
            // return this.originalRowsAllTrades.map(row => parseFloat(row['PandL'])).filter(v => !isNaN(v));

            return this.originalRowsAllTrades
                .map(row => parseFloat(row['profit']))
                .filter(v => !isNaN(v));

        } else if (this.dataKey === 'modelAccuracy') {
            return this.originalRowsAllTradesModelAccuracy.map(row => parseFloat(row['error'])).filter(v => !isNaN(v));
        } else {
            return [];
        }
    }


    private filterRows(min: number, max: number): void {
        const isLastInterval = this.isLastInterval(min, max);

        let filteredRows: TableRow[] = [];

        

        if (this.dataKey === 'tradingStrategy') {
            filteredRows = this.originalRowsAllTrades.filter(row => {
                const pandl = parseFloat(row['profit']);
                return isLastInterval ? pandl >= min && pandl <= max : pandl >= min && pandl < max;
            });
            this.rowsAllTrades.set(filteredRows); // Actualiza la señal
            this.sharedDataService.setRowsAllTrades(filteredRows);
        } 
        else if (this.dataKey === 'modelAccuracy') {
            filteredRows = this.originalRowsAllTradesModelAccuracy.filter(row => {
                const error = parseFloat(row['error']);
                return isLastInterval ? error >= min && error <= max : error >= min && error < max;
            });

            this.rowsAllTradesModelAccuracy.set(filteredRows); // Actualiza la señal
            this.sharedDataService.setRowsAllTradesModelAccuracy(filteredRows);
        }
    }

    private isLastInterval(min: number, max: number): boolean {
        const pandlValues = this.getPandLValues();
        const maxValue = Math.max(...pandlValues);
        return max >= maxValue;
    }


}
